import {
  BugIcon,
  ChartNoAxesColumnIcon,
  PlugIcon,
  GitPullRequestIcon,
  SettingsIcon,
  type LucideIcon,
} from "lucide-react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

import {
  fetchCurrentOriginAccountIdentity,
  logoutPrimarySession,
} from "../../environments/primary";
import { useCmmdWorkIntakeSnapshot } from "~/cmmd/workIntakeResource";
import { cn } from "~/lib/utils";
import { useHasPullRequestCapableEnvironment } from "~/pullRequest/pullRequestEnvironments";
import { RESPONSIVE_ICON_BUTTON_CLASS } from "~/lib/touchTarget";
import { useCloseSidebarOnNavigate } from "./useCloseSidebarOnNavigate";
import { FeedbackDialog } from "./FeedbackDialog";
import { PersonAvatar } from "../identity/PersonAvatar";
import { SidebarFooter } from "../ui/sidebar";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import {
  resolveAccountDisplayName,
  resolveAccountAvatarUrl,
  resolveCmmdIdentityDisplayName,
  shouldRefreshPrimaryAccountIdentity,
} from "./sidebarAccountFooter.logic";
import { CMMD_RUNTIME_ENVIRONMENT_PREFIX } from "../../environments/cmmdRuntimeEnvironmentId";
import { cmmdEmbedded } from "../../embed/cmmdEmbedMode";
import { subscribeCmmdEmbedHostCommands } from "../../embed/cmmdEmbedHostCommands";

type LocalAccountState = {
  readonly displayName: string;
  readonly avatarUrl: string | null;
};

const DEFAULT_LOCAL_STATE: LocalAccountState = {
  displayName: "Authenticated user",
  avatarUrl: null,
};

export const SidebarAccountFooter = memo(function SidebarAccountFooter() {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const navigate = useNavigate();
  const embedded = cmmdEmbedded();
  const { connected, identity } = useCmmdWorkIntakeSnapshot();
  const feedbackSenderName =
    connected && identity ? resolveCmmdIdentityDisplayName(identity) : null;

  useEffect(() => {
    if (!embedded) return;
    return subscribeCmmdEmbedHostCommands({
      onNavigate: (path, search) => {
        switch (path) {
          case "/settings":
            void navigate({ to: "/settings" });
            return;
          case "/plugins":
            void navigate({ to: "/plugins" });
            return;
          case "/usage":
            void navigate({ to: "/usage" });
            return;
          case "/pull-requests":
            void navigate({
              to: "/pull-requests",
              search: { state: search?.state === "closed" ? "closed" : "open" },
            });
            return;
          default:
            break;
        }
        // CMMD project task open-work → real Forge thread viewer.
        const threadMatch = new RegExp(
          `^/(${CMMD_RUNTIME_ENVIRONMENT_PREFIX}[A-Za-z0-9][A-Za-z0-9._-]{0,127})/([A-Za-z0-9][A-Za-z0-9._-]{0,127})$`,
        ).exec(path);
        if (threadMatch) {
          void navigate({
            to: "/$environmentId/$threadId",
            params: {
              environmentId: threadMatch[1]!,
              threadId: threadMatch[2]!,
            },
          });
        }
      },
      onOpenFeedback: () => {
        setFeedbackOpen(true);
      },
    });
  }, [embedded, navigate]);

  // Embedded: restore PR / bug / plug / usage in the sidebar. CMMD AppBar owns
  // Focus, Help, and Gear (settings). Avatar stays hidden inside the iframe.
  if (embedded) {
    return (
      <SidebarFooter>
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-1">
          <FooterNavigation
            onOpenFeedback={() => setFeedbackOpen(true)}
            hideSettings
            includeUsage
          />
          <FeedbackDialog
            open={feedbackOpen}
            onOpenChange={setFeedbackOpen}
            {...(feedbackSenderName ? { senderName: feedbackSenderName } : {})}
          />
        </div>
      </SidebarFooter>
    );
  }

  return (
    <SidebarFooter>
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-1">
        <FooterNavigation onOpenFeedback={() => setFeedbackOpen(true)} />
        <AccountRow feedbackOpen={feedbackOpen} onFeedbackOpenChange={setFeedbackOpen} />
      </div>
    </SidebarFooter>
  );
});

/**
 * Compact utility controls preserve the destinations while keeping the footer
 * visually subordinate to the thread list. Tooltip labels retain the full
 * meaning for pointer and keyboard users.
 */
interface FooterNavigationProps {
  readonly onOpenFeedback: () => void;
  readonly hideSettings?: boolean;
  readonly includeUsage?: boolean;
}

const FooterNavigation = memo(function FooterNavigation({
  onOpenFeedback,
  hideSettings = false,
  includeUsage = false,
}: FooterNavigationProps) {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const closeMobileSidebar = useCloseSidebarOnNavigate();
  const hasPullRequests = useHasPullRequestCapableEnvironment();
  const handlePullRequestsClick = useCallback(() => {
    void navigate({ to: "/pull-requests", search: { state: "open" } });
    closeMobileSidebar();
  }, [closeMobileSidebar, navigate]);
  const handlePluginsClick = useCallback(() => {
    void navigate({ to: "/plugins" });
    closeMobileSidebar();
  }, [closeMobileSidebar, navigate]);
  const handleSettingsClick = useCallback(() => {
    void navigate({ to: "/settings" });
    closeMobileSidebar();
  }, [closeMobileSidebar, navigate]);
  const handleUsageClick = useCallback(() => {
    void navigate({ to: "/usage" });
    closeMobileSidebar();
  }, [closeMobileSidebar, navigate]);

  return (
    <div
      className="flex items-center justify-between gap-0.5 md:justify-start"
      data-testid="sidebar-utility-controls"
    >
      {hideSettings ? null : (
        <SidebarUtilityIconButton
          icon={SettingsIcon}
          label="Settings"
          selected={pathname.startsWith("/settings")}
          testId="sidebar-settings-link"
          onClick={handleSettingsClick}
        />
      )}
      {hasPullRequests ? (
        <SidebarUtilityIconButton
          icon={GitPullRequestIcon}
          label="Pull requests"
          selected={pathname === "/pull-requests"}
          testId="sidebar-pull-requests-link"
          onClick={handlePullRequestsClick}
        />
      ) : null}
      <SidebarUtilityIconButton
        icon={BugIcon}
        label="Report a bug or suggest a feature"
        mobileLabel="Report bug"
        testId="sidebar-feedback-button"
        onClick={onOpenFeedback}
      />
      <SidebarUtilityIconButton
        icon={PlugIcon}
        label="Plugins"
        testId="sidebar-plugins-link"
        onClick={handlePluginsClick}
      />
      {includeUsage ? (
        <SidebarUtilityIconButton
          icon={ChartNoAxesColumnIcon}
          label="Usage"
          testId="sidebar-usage-link"
          onClick={handleUsageClick}
        />
      ) : null}
    </div>
  );
});

interface SidebarUtilityIconButtonProps {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly mobileLabel?: string;
  readonly onClick: () => void;
  readonly selected?: boolean;
  readonly testId: string;
}

function SidebarUtilityIconButton({
  icon: Icon,
  label,
  mobileLabel,
  onClick,
  selected = false,
  testId,
}: SidebarUtilityIconButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            aria-label={label}
            aria-current={selected ? "page" : undefined}
            className={cn(
              FOOTER_ICON_BUTTON_CLASS,
              selected ? "bg-sidebar-accent text-sidebar-foreground" : null,
            )}
            data-testid={testId}
            onClick={onClick}
          >
            <Icon aria-hidden="true" className="size-4" />
            <span className="text-sm leading-none md:sr-only">{mobileLabel ?? label}</span>
          </button>
        }
      />
      <TooltipPopup side="top">{label}</TooltipPopup>
    </Tooltip>
  );
}

const FOOTER_ICON_BUTTON_CLASS = cn(
  RESPONSIVE_ICON_BUTTON_CLASS,
  "flex-col gap-0.5 text-sidebar-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:flex-row",
);

interface AccountRowProps {
  readonly feedbackOpen: boolean;
  readonly onFeedbackOpenChange: (open: boolean) => void;
}

const AccountRow = memo(function AccountRow({
  feedbackOpen,
  onFeedbackOpenChange,
}: AccountRowProps) {
  const navigate = useNavigate();
  const closeMobileSidebar = useCloseSidebarOnNavigate();
  const [localAccount, setLocalAccount] = useState<LocalAccountState>(DEFAULT_LOCAL_STATE);

  const { connected, identity } = useCmmdWorkIntakeSnapshot();
  // Derive display values — CMMD identity takes priority when connected.
  const displayName = useMemo(() => {
    if (connected && identity) {
      return (
        resolveCmmdIdentityDisplayName(identity) ??
        resolveAccountDisplayName(localAccount.displayName)
      );
    }
    return resolveAccountDisplayName(localAccount.displayName);
  }, [connected, identity, localAccount.displayName]);

  const avatarUrl = useMemo(() => {
    return resolveAccountAvatarUrl({
      connected,
      cmmdAvatarUrl: identity?.avatarUrl,
      localAvatarUrl: localAccount.avatarUrl,
    });
  }, [connected, identity?.avatarUrl, localAccount.avatarUrl]);

  const feedbackSenderName =
    connected && identity ? resolveCmmdIdentityDisplayName(identity) : null;

  // Keep a primary-account fallback for incomplete CMMD profiles, e.g. when
  // CMMD only returns a synthetic username such as `user:103`.
  useEffect(() => {
    const shouldFetch =
      localAccount.displayName === DEFAULT_LOCAL_STATE.displayName ||
      shouldRefreshPrimaryAccountIdentity({
        connected,
        cmmdAvatarUrl: identity?.avatarUrl,
        localAvatarUrl: localAccount.avatarUrl,
      });
    if (!shouldFetch) return;

    let active = true;
    void fetchCurrentOriginAccountIdentity()
      .then((id) => {
        if (!active) return;
        if (!id.verified) {
          void logoutPrimarySession()
            .catch(() => undefined)
            .finally(() => {
              if (!active) return;
              void navigate({ to: "/login", replace: true, reloadDocument: true });
            });
          return;
        }
        setLocalAccount({
          displayName: id.displayName,
          avatarUrl: id.avatarUrl,
        });
      })
      .catch(() => {
        if (!active) return;
        setLocalAccount(DEFAULT_LOCAL_STATE);
      });
    return () => {
      active = false;
    };
  }, [connected, identity?.avatarUrl, localAccount.avatarUrl, localAccount.displayName, navigate]);

  const handleProfileClick = useCallback(() => {
    void navigate({ to: "/settings/profile" });
    closeMobileSidebar();
  }, [closeMobileSidebar, navigate]);
  const handleUsageClick = useCallback(() => {
    void navigate({ to: "/usage" });
    closeMobileSidebar();
  }, [closeMobileSidebar, navigate]);

  return (
    <>
      <FeedbackDialog
        open={feedbackOpen}
        onOpenChange={onFeedbackOpenChange}
        {...(feedbackSenderName ? { senderName: feedbackSenderName } : {})}
      />
      <div data-testid="sidebar-account-row" className="flex items-center justify-end gap-0.5">
        <SidebarUtilityIconButton
          icon={ChartNoAxesColumnIcon}
          label="Usage"
          testId="sidebar-usage-link"
          onClick={handleUsageClick}
        />
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                aria-label={`Open profile for ${displayName}`}
                data-testid="sidebar-profile-link"
                onClick={handleProfileClick}
                className={cn(
                  RESPONSIVE_ICON_BUTTON_CLASS,
                  "flex-col gap-0.5 rounded-full text-sidebar-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:flex-row",
                )}
              >
                <PersonAvatar
                  name={displayName}
                  avatarUrl={avatarUrl}
                  className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground"
                />
                <span className="text-sm leading-none md:sr-only">Profile</span>
              </button>
            }
          />
          <TooltipPopup side="top">Open profile for {displayName}</TooltipPopup>
        </Tooltip>
      </div>
    </>
  );
});
